<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProjectDeliverablesAPI from 'dashboard/api/projectDeliverables';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
  phases: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const STATUSES = [
  'not_started',
  'in_progress',
  'submitted',
  'accepted',
  'rejected',
];

const STATUS_CLASS = {
  not_started: 'bg-n-alpha-1 text-n-slate-11',
  in_progress: 'bg-n-blue-3 text-n-blue-11',
  submitted: 'bg-n-amber-3 text-n-amber-11',
  accepted: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
};

const deliverables = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showForm = ref(false);
const uploadingId = ref(null);
const fileInputs = ref({});

const blankForm = () => ({
  name: '',
  description: '',
  status: 'not_started',
  due_date: '',
  acceptance_criteria: '',
  project_phase_id: '',
});

const form = ref(blankForm());

const phaseName = phaseId => {
  const phase = props.phases.find(p => p.id === phaseId);
  return phase ? phase.name : '';
};

const statusClass = status => STATUS_CLASS[status] || STATUS_CLASS.not_started;
const statusLabel = status =>
  t(`PROJECTS.DELIVERABLES.STATUS.${status.toUpperCase()}`);

const loadDeliverables = async () => {
  isLoading.value = true;
  try {
    const { data } = await ProjectDeliverablesAPI.get(props.projectId);
    deliverables.value = data || [];
  } catch (error) {
    deliverables.value = [];
    useAlert(t('PROJECTS.DELIVERABLES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  form.value = blankForm();
  showForm.value = false;
};

const createDeliverable = async () => {
  const name = form.value.name.trim();
  if (!name || isSaving.value) return;
  isSaving.value = true;
  try {
    const payload = {
      name,
      description: form.value.description.trim() || null,
      status: form.value.status,
      due_date: form.value.due_date || null,
      acceptance_criteria: form.value.acceptance_criteria.trim() || null,
    };
    if (form.value.project_phase_id) {
      payload.project_phase_id = Number(form.value.project_phase_id);
    }
    await ProjectDeliverablesAPI.create(props.projectId, payload);
    resetForm();
    await loadDeliverables();
  } catch (error) {
    useAlert(t('PROJECTS.DELIVERABLES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const acceptDeliverable = async deliverable => {
  try {
    await ProjectDeliverablesAPI.update(props.projectId, deliverable.id, {
      status: 'accepted',
    });
    await loadDeliverables();
  } catch (error) {
    useAlert(t('PROJECTS.DELIVERABLES.SAVE_ERROR'));
  }
};

const deleteDeliverable = async deliverable => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.DELIVERABLES.DELETE_CONFIRM'))) return;
  try {
    await ProjectDeliverablesAPI.delete(props.projectId, deliverable.id);
    deliverables.value = deliverables.value.filter(
      d => d.id !== deliverable.id
    );
  } catch (error) {
    useAlert(t('PROJECTS.DELIVERABLES.DELETE_ERROR'));
  }
};

const onUploadClick = deliverableId => {
  fileInputs.value[deliverableId]?.click();
};

const onFileInput = async (e, deliverable) => {
  const files = Array.from(e.target.files);
  e.target.value = '';
  if (!files.length) return;
  uploadingId.value = deliverable.id;
  try {
    await files.reduce(
      (chain, file) =>
        chain.then(() =>
          ProjectDeliverablesAPI.uploadFile(
            props.projectId,
            deliverable.id,
            file
          )
        ),
      Promise.resolve()
    );
    await loadDeliverables();
  } catch (error) {
    useAlert(t('PROJECTS.DELIVERABLES.UPLOAD_ERROR'));
  } finally {
    uploadingId.value = null;
  }
};

const removeFile = async (deliverable, file) => {
  try {
    await ProjectDeliverablesAPI.removeFile(
      props.projectId,
      deliverable.id,
      file.id
    );
    await loadDeliverables();
  } catch (error) {
    useAlert(t('PROJECTS.DELIVERABLES.FILE_DELETE_ERROR'));
  }
};

const hasDeliverables = computed(() => deliverables.value.length > 0);

watch(() => props.projectId, loadDeliverables);
onMounted(loadDeliverables);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('PROJECTS.DELIVERABLES.TITLE') }}
      </h3>
      <Button
        variant="faded"
        color="blue"
        size="sm"
        :icon="showForm ? 'i-lucide-x' : 'i-lucide-plus'"
        :label="
          showForm
            ? t('PROJECTS.DELIVERABLES.CANCEL')
            : t('PROJECTS.DELIVERABLES.ADD')
        "
        @click="showForm ? resetForm() : (showForm = true)"
      />
    </div>

    <!-- Create form -->
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <input
        v-model="form.name"
        type="text"
        :placeholder="t('PROJECTS.DELIVERABLES.NAME_PLACEHOLDER')"
        class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <textarea
        v-model="form.description"
        rows="2"
        :placeholder="t('PROJECTS.DELIVERABLES.DESCRIPTION_PLACEHOLDER')"
        class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          {{ t('PROJECTS.DELIVERABLES.FIELDS.STATUS') }}
          <select
            v-model="form.status"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="s in STATUSES" :key="s" :value="s">
              {{ statusLabel(s) }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          {{ t('PROJECTS.DELIVERABLES.FIELDS.DUE_DATE') }}
          <input
            v-model="form.due_date"
            type="date"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
        </label>
      </div>
      <label
        v-if="phases.length"
        class="flex flex-col gap-1 text-xs text-n-slate-10"
      >
        {{ t('PROJECTS.DELIVERABLES.FIELDS.PHASE') }}
        <select
          v-model="form.project_phase_id"
          class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
        >
          <option value="">
            {{ t('PROJECTS.DELIVERABLES.NO_PHASE') }}
          </option>
          <option v-for="phase in phases" :key="phase.id" :value="phase.id">
            {{ phase.name }}
          </option>
        </select>
      </label>
      <textarea
        v-model="form.acceptance_criteria"
        rows="2"
        :placeholder="t('PROJECTS.DELIVERABLES.ACCEPTANCE_PLACEHOLDER')"
        class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('PROJECTS.DELIVERABLES.CANCEL')"
          @click="resetForm"
        />
        <Button
          size="sm"
          color="blue"
          icon="i-lucide-check"
          :label="t('PROJECTS.DELIVERABLES.SAVE')"
          :is-loading="isSaving"
          :disabled="!form.name.trim()"
          @click="createDeliverable"
        />
      </div>
    </div>

    <!-- Empty state -->
    <ProjectEmptyState
      v-if="!hasDeliverables && !isLoading"
      icon="i-lucide-package"
      :title="t('PROJECTS.DELIVERABLES.EMPTY')"
    >
      <template v-if="!showForm" #action>
        <Button
          size="sm"
          color="blue"
          icon="i-lucide-plus"
          :label="t('PROJECTS.DELIVERABLES.ADD')"
          @click="showForm = true"
        />
      </template>
    </ProjectEmptyState>

    <!-- List -->
    <ul v-else class="flex flex-col gap-3">
      <li
        v-for="deliverable in deliverables"
        :key="deliverable.id"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col min-w-0 gap-1">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ deliverable.name }}
              </span>
              <span
                class="inline-flex items-center px-2 py-0.5 text-xs rounded-md shrink-0"
                :class="statusClass(deliverable.status)"
              >
                {{ statusLabel(deliverable.status) }}
              </span>
            </div>
            <div
              class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-10"
            >
              <span v-if="deliverable.due_date" class="flex items-center gap-1">
                <span class="i-lucide-calendar size-3.5" />
                {{ deliverable.due_date }}
              </span>
              <span
                v-if="deliverable.project_phase_id"
                class="flex items-center gap-1"
              >
                <span class="i-lucide-list-ordered size-3.5" />
                {{ phaseName(deliverable.project_phase_id) }}
              </span>
              <span class="flex items-center gap-1">
                <span class="i-lucide-paperclip size-3.5" />
                {{
                  t('PROJECTS.DELIVERABLES.FILE_COUNT', {
                    count: (deliverable.files || []).length,
                  })
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <Button
              v-if="deliverable.status !== 'accepted'"
              variant="faded"
              color="teal"
              size="xs"
              icon="i-lucide-check"
              :label="t('PROJECTS.DELIVERABLES.ACCEPT')"
              @click="acceptDeliverable(deliverable)"
            />
            <Button
              v-tooltip.top="t('PROJECTS.DELIVERABLES.DELETE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              @click="deleteDeliverable(deliverable)"
            />
          </div>
        </div>

        <!-- Files -->
        <ul
          v-if="(deliverable.files || []).length"
          class="flex flex-col divide-y divide-n-weak/60"
        >
          <li
            v-for="file in deliverable.files"
            :key="file.id"
            class="flex items-center gap-2.5 py-1.5 group"
          >
            <span class="shrink-0 i-lucide-file size-4 text-n-slate-10" />
            <a
              :href="file.file_url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex-1 min-w-0 text-sm truncate text-n-slate-12 hover:text-n-blue-text"
            >
              {{ file.filename }}
            </a>
            <Button
              v-tooltip.top="t('PROJECTS.DELIVERABLES.REMOVE_FILE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-x"
              class="transition-opacity opacity-0 shrink-0 group-hover:opacity-100"
              @click="removeFile(deliverable, file)"
            />
          </li>
        </ul>

        <!-- Upload -->
        <div>
          <Button
            variant="faded"
            color="slate"
            size="xs"
            :icon="
              uploadingId === deliverable.id
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-upload'
            "
            :label="
              uploadingId === deliverable.id
                ? t('PROJECTS.DELIVERABLES.UPLOADING')
                : t('PROJECTS.DELIVERABLES.UPLOAD_FILE')
            "
            :disabled="uploadingId === deliverable.id"
            @click="onUploadClick(deliverable.id)"
          />
          <input
            :ref="el => (fileInputs[deliverable.id] = el)"
            type="file"
            multiple
            class="sr-only"
            @change="onFileInput($event, deliverable)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
